import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Globe2,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useLanguage } from '../../contexts/LanguageContext';

const SECTION_LINKS = [
  { id: 'features', labelKey: 'landing.nav.features' },
  { id: 'how-it-works', labelKey: 'landing.nav.howItWorks' },
  { id: 'use-cases', labelKey: 'landing.nav.forWho' },
];

export default function PublicSiteHeader() {
  const {
    t,
    language,
    changeLanguage,
    supportedLanguages,
  } = useLanguage();

  const location = useLocation();
  const languageRef = useRef(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const selectedLanguage =
    supportedLanguages.find((item) => item.code === language) ||
    supportedLanguages[0];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      setMobileOpen(false);
      setLanguageOpen(false);
    };

    const handlePointerDown = (event) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target)
      ) {
        setLanguageOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        setLanguageOpen(false);
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLanguageOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header
      className={`public-site-header${isScrolled ? ' is-scrolled' : ''}`}
    >
      <div className="public-site-container public-site-header__inner">
        <Link
          to="/"
          className="public-site-brand"
          aria-label={t('landing.brand.homeAria')}
        >
          <img
            src="/logo.png"
            alt="Lmenu"
            className="public-site-brand__logo"
          />
        </Link>

        <nav
          className={`public-site-nav${mobileOpen ? ' is-open' : ''}`}
          aria-label={t('landing.nav.aria')}
        >
          {SECTION_LINKS.map((item) => (
            <Link key={item.id} to={`/#${item.id}`}>
              {t(item.labelKey)}
            </Link>
          ))}

          <Link
            to="/free-trial"
            className="public-site-nav__trial-mobile"
          >
            {t('landing.freeTrial.cta')}
          </Link>
        </nav>

        <div className="public-site-header__actions">
          <div
            className="public-site-language"
            ref={languageRef}
          >
            <button
              type="button"
              className="public-site-language__trigger"
              onClick={() =>
                setLanguageOpen((current) => !current)
              }
              aria-expanded={languageOpen}
              aria-haspopup="menu"
              aria-label={t('landing.languageAria')}
            >
              <Globe2 size={16} />

              <span>
                {selectedLanguage?.code?.toUpperCase()}
              </span>

              <ChevronDown
                size={14}
                className={languageOpen ? 'is-open' : ''}
              />
            </button>

            {languageOpen ? (
              <div
                className="public-site-language__menu"
                role="menu"
              >
                {supportedLanguages.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={language === item.code}
                    className={
                      language === item.code ? 'is-active' : ''
                    }
                    onClick={() => {
                      changeLanguage(item.code);
                      setLanguageOpen(false);
                    }}
                  >
                    <span>
                      {item.code.toUpperCase()}
                    </span>

                    <strong>
                      {t(`landing.languages.${item.code}`)}
                    </strong>

                    {language === item.code ? (
                      <Check size={14} />
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <Link
            to="/free-trial"
            className="public-site-trial-button"
          >
            {t('landing.freeTrial.cta')}
            <ArrowRight size={16} />
          </Link>

          <button
            type="button"
            className="public-site-mobile-menu"
            onClick={() =>
              setMobileOpen((current) => !current)
            }
            aria-label={t('landing.nav.mobileMenuAria')}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X size={19} />
            ) : (
              <MenuIcon size={19} />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}